/**
 * Verzendingen die nog onderweg zijn (sessie 72, blok 1).
 *
 * Versturen zet een rij in de uitgaande wachtrij; de job verstuurt pas na
 * het ongedaan-venster (30 s) en daarna Gmail of WhatsApp. Het scherm
 * bevestigt meteen: het vak is leeg en in de zaak staat het bericht als
 * "wordt verzonden". Deze kleine winkel houdt die regels bij, per zaak.
 *
 * Hoe een regel verder gaat:
 * - een live-event `antwoord` of `verzendstatus` van de zaak, of het moment
 *   waarop het venster voorbij is, laat de volger één keer de wachtrij van
 *   die zaak lezen (`GET /api/werkplek/zaken/{id}/wachtrij`);
 * - verstuurd: de zaak wordt één keer opnieuw opgehaald en de regel maakt
 *   plaats voor het echte bericht;
 * - mislukt: de regel wordt "Versturen mislukt: <reden>" en de tekst komt
 *   terug in het vak (alleen als dat leeg is; wat JP intussen typte blijft);
 * - ingetrokken (Ongedaan maken): de regel gaat weg, de tekst komt terug.
 *
 * Waarom ook de wachtrij lezen en niet alleen op events wachten: de job kan
 * in een ander proces draaien dan de webserver, en dan komt zijn event nooit
 * op de stroom van dit scherm. Het is één aanvraag per verzending (plus een
 * paar herhalingen zolang de rij nog wacht), geen polling van het scherm.
 *
 * De winkel hoort bij de QueryClient van het scherm (één per client), zodat
 * hij met de rest van de cache leeft en tests elkaar niet raken.
 */
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useCallback, useSyncExternalStore } from "react";

export type VerzendStand = "wacht" | "verzonden" | "mislukt" | "ingetrokken";

export interface Verzending {
  wachtrijId: string;
  zaakId: string;
  kanaal: string;
  tekst: string;
  onderwerp: string | null;
  /** Wanneer het venster sluit en de job hem oppakt (ms sinds epoch). */
  verstuurtOp: number;
  /** Later versturen (vandaag 17:00, morgen 08:00). */
  gepland: boolean;
  stand: VerzendStand;
  reden: string | null;
  /** De tekst is (na mislukt of ingetrokken) al teruggezet of bewust niet. */
  hersteld: boolean;
}

const LEEG: Verzending[] = [];

interface Winkel {
  perZaak: Map<string, Verzending[]>;
  luisteraars: Set<() => void>;
}

const winkels = new WeakMap<QueryClient, Winkel>();

function winkel(qc: QueryClient): Winkel {
  let w = winkels.get(qc);
  if (!w) {
    w = { perZaak: new Map(), luisteraars: new Set() };
    winkels.set(qc, w);
  }
  return w;
}

function zetLijst(qc: QueryClient, zaakId: string, lijst: Verzending[]): void {
  const w = winkel(qc);
  const nieuw = new Map(w.perZaak);
  if (lijst.length) nieuw.set(zaakId, lijst);
  else nieuw.delete(zaakId);
  w.perZaak = nieuw;
  for (const l of w.luisteraars) l();
}

/** Een nieuwe verzending; oude afgeronde regels van deze zaak gaan weg. */
export function voegVerzendingToe(
  qc: QueryClient,
  v: Omit<Verzending, "stand" | "reden" | "hersteld">,
): void {
  const oud = verzendingenVan(qc, v.zaakId).filter(
    (x) => x.stand === "wacht" && x.wachtrijId !== v.wachtrijId,
  );
  zetLijst(qc, v.zaakId, [...oud, { ...v, stand: "wacht", reden: null, hersteld: false }]);
}

export function zetVerzending(qc: QueryClient, wachtrijId: string, patch: Partial<Verzending>): void {
  for (const [zaakId, lijst] of winkel(qc).perZaak) {
    if (!lijst.some((v) => v.wachtrijId === wachtrijId)) continue;
    zetLijst(
      qc,
      zaakId,
      lijst.map((v) => (v.wachtrijId === wachtrijId ? { ...v, ...patch } : v)),
    );
    return;
  }
}

export function verwijderVerzending(qc: QueryClient, wachtrijId: string): void {
  for (const [zaakId, lijst] of winkel(qc).perZaak) {
    if (!lijst.some((v) => v.wachtrijId === wachtrijId)) continue;
    zetLijst(
      qc,
      zaakId,
      lijst.filter((v) => v.wachtrijId !== wachtrijId),
    );
    return;
  }
}

export function verzendingenVan(qc: QueryClient, zaakId: string): Verzending[] {
  return winkel(qc).perZaak.get(zaakId) ?? LEEG;
}

function useAbonneer(qc: QueryClient) {
  return useCallback(
    (l: () => void) => {
      const w = winkel(qc);
      w.luisteraars.add(l);
      return () => {
        w.luisteraars.delete(l);
      };
    },
    [qc],
  );
}

/** De regels van één zaak; rendert opnieuw bij elke wijziging. */
export function useVerzendingen(zaakId: string | null): Verzending[] {
  const qc = useQueryClient();
  const abonneer = useAbonneer(qc);
  return useSyncExternalStore(
    abonneer,
    () => (zaakId ? verzendingenVan(qc, zaakId) : LEEG),
    () => LEEG,
  );
}

/** Alle regels (voor de volger). */
export function useAlleVerzendingen(): Map<string, Verzending[]> {
  const qc = useQueryClient();
  const abonneer = useAbonneer(qc);
  return useSyncExternalStore(
    abonneer,
    () => winkel(qc).perZaak,
    () => winkel(qc).perZaak,
  );
}
